export default function toTitleCase(str) {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Escapes HTML special characters in a string to prevent XSS when inserting
 * untrusted text into HTML strings (e.g. tooltip content).
 * @param {*} value - The value to escape (coerced to string)
 * @returns {string} - HTML-escaped string
 */
export function escapeHtml(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Turkish-aware text formatter for filtering and text comparison
 * Normalizes Turkish characters for proper filtering and search operations
 * Preserves the distinction between dotted (İ/i) and undotted (I/ı) character pairs
 * @param {string} text - The text to normalize
 * @returns {string} - Normalized text for filtering
 */
export function turkishAwareTextFormatter(text) {
  if (!text || typeof text !== 'string') return text;

  // Handle Turkish character pairs properly:
  // İ (dotted capital) and i (dotted lowercase) should match each other
  // I (undotted capital) and ı (undotted lowercase) should match each other
  let normalized = text
    // Handle dotted I pair: İ ↔ i (both normalize to 'i')
    .replace(/İ/g, 'i') // Turkish capital İ to lowercase i

    // Handle undotted I pair: I ↔ ı (both normalize to 'ı')
    .replace(/I/g, 'ı') // Regular capital I to lowercase ı

    // Handle other Turkish characters (normalize to lowercase)
    .replace(/Ğ/g, 'ğ') // Capital Ğ to lowercase ğ
    .replace(/Ü/g, 'ü') // Capital Ü to lowercase ü
    .replace(/Ş/g, 'ş') // Capital Ş to lowercase ş
    .replace(/Ö/g, 'ö') // Capital Ö to lowercase ö
    .replace(/Ç/g, 'ç'); // Capital Ç to lowercase ç

  // Apply Turkish locale-aware lowercase conversion for remaining characters
  normalized = normalized.toLocaleLowerCase('tr-TR');

  return normalized;
}

/**
 * Converts Turkish characters to their ASCII equivalents for search purposes
 * This allows "Istanbul" to match "İstanbul" and vice versa
 * @param {string} text - The text to convert
 * @returns {string} - Text with Turkish characters replaced by ASCII equivalents
 */
export function turkishToAscii(text) {
  if (!text || typeof text !== 'string') return text;

  return text
    .replace(/İ/g, 'I')
    .replace(/ı/g, 'i')
    .replace(/Ğ/g, 'G')
    .replace(/ğ/g, 'g')
    .replace(/Ü/g, 'U')
    .replace(/ü/g, 'u')
    .replace(/Ş/g, 'S')
    .replace(/ş/g, 's')
    .replace(/Ș/g, 'S')
    .replace(/ș/g, 's')
    .replace(/Ţ/g, 'T')
    .replace(/ţ/g, 't')
    .replace(/Ț/g, 'T')
    .replace(/ț/g, 't')
    .replace(/Ö/g, 'O')
    .replace(/ö/g, 'o')
    .replace(/Ç/g, 'C')
    .replace(/ç/g, 'c');
}

/**
 * Turkish-aware multiselect filter function
 * Filters an array of options based on Turkish-normalized text matching
 * @param {Array} options - Array of options to filter
 * @param {string} search - Search string from user input
 * @param {string} labelKey - Property name to search in for objects
 * @returns {Array} - Filtered options
 */
export function turkishAwareMultiselectFilter(options, search, labelKey = 'label') {
  if (!search || !Array.isArray(options)) return options;

  const normalizedSearch = turkishAwareTextFormatter(search);

  return options.filter((option) => {
    // Get the text to search in
    let searchText = '';
    if (typeof option === 'string') {
      searchText = option;
    } else if (typeof option === 'object' && option !== null) {
      searchText = option[labelKey] || '';
    }

    // Normalize the option text and check if it includes the search term
    const normalizedText = turkishAwareTextFormatter(String(searchText));
    return normalizedText.includes(normalizedSearch);
  });
}
